import React, { useState, useEffect } from 'react';
import styles from '../AssignedJobsSection.module.css';
import { getEpisodeRejectionRows } from '../episodeErrors';
import { JOB_STAGES, stageByKey, TIMELINE_DOT_COLOR, fmtHrs, fmtUSD, jobHoursTarget, taskUrl } from './jobsApi';
import RejectedEpisodePreview from '../../../RoboticData/RejectedEpisodePreview';
import { ChipExtIcon } from './JobRowCells';

// ── Job detail drawer ────────────────────────────────────────────────────────

const JobDetailDrawer = ({ job, onClose, getHeaders, jobsBaseUrl }) => {
    const [episodes, setEpisodes] = useState([]);
    const [stageCounts, setStageCounts] = useState({});
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('all');
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setFilter('all');
        setExpandedId(null);
        fetch(`${jobsBaseUrl}/${job.job_id}/episodes`, { headers: getHeaders() })
            .then((res) => res.json())
            .then((json) => {
                if (cancelled) return;
                setEpisodes(json.data || []);
                setStageCounts(json.stage_counts || {});
            })
            .catch(() => { if (!cancelled) { setEpisodes([]); setStageCounts({}); } })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [job.job_id, jobsBaseUrl, getHeaders]);

    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const total = episodes.length;
    const filtered = filter === 'all' ? episodes : episodes.filter((e) => e.stage === filter);
    const target = jobHoursTarget(job);

    return (
        <div className={styles.drawerOverlay} onClick={onClose}>
            <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.drawerHdr}>
                    <div className={styles.drawerTopRow}>
                        <div className={styles.drawerTitle}>
                            {job.job_id} · {job.tasks[0]?.task_name || '—'}
                            {job.tasks.length > 1 ? ` +${job.tasks.length - 1}` : ''}
                        </div>
                        <button className={styles.dClose} onClick={onClose}>×</button>
                    </div>
                    <div className={styles.dMeta}>
                        <div className={styles.dMetaItem}><span className={styles.dMetaLbl}>Job ID</span><span className={styles.dMetaVal}>{job.job_id}</span></div>
                        {job.order_id != null && (
                            <div className={styles.dMetaItem}><span className={styles.dMetaLbl}>Order</span><span className={styles.dMetaVal}>ORD-{job.order_id}</span></div>
                        )}
                        {job.robot && (
                            <div className={styles.dMetaItem}>
                                <span className={styles.dMetaLbl}>Robot</span>
                                <span className={styles.dMetaVal}>{job.robot.product_name}</span>
                                {job.robot.category && <span className={styles.dMetaSub}>{job.robot.category}</span>}
                            </div>
                        )}
                        <div className={styles.dMetaItem}><span className={styles.dMetaLbl}>Assigned</span><span className={styles.dMetaVal}>{job.created_at.slice(0, 10)}</span></div>
                        <div className={styles.dMetaItem}><span className={styles.dMetaLbl}>Due</span><span className={styles.dMetaVal}>{job.due_date || '—'}</span></div>
                        <div className={styles.dMetaItem}><span className={styles.dMetaLbl}>Hours</span><span className={styles.dMetaVal}>{fmtHrs(job.hours_logged)} / {fmtHrs(target)} h</span></div>
                        <div className={styles.dMetaItem}>
                            <span className={styles.dMetaLbl}>Amount</span>
                            <span className={styles.dMetaVal}>{fmtUSD(job.amount_earned_total)}</span>
                        </div>
                    </div>
                    <div className={styles.jTasks}>
                        {job.tasks.map((t) => (
                            <a
                                key={t.task_id}
                                className={`${styles.jobChip} ${styles.jobChipLink}`}
                                href={taskUrl(t.task_id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={`Open the ${t.task_name} upload page`}
                            >
                                {t.task_name}<span className={styles.chipHrs}>{fmtHrs(t.hours_logged)}h / {fmtHrs(t.assigned_hours)}h</span>
                                <ChipExtIcon />
                            </a>
                        ))}
                    </div>
                </div>

                <div className={styles.jSummary}>
                    <div className={styles.jStats}>
                        {JOB_STAGES.map((s) => (
                            <div className={styles.jStat} key={s.key}>
                                <span className={styles.jStatDot} style={{ background: s.color }} />
                                <span className={styles.jStatN}>{stageCounts[s.key] || 0}</span> {s.label}
                            </div>
                        ))}
                    </div>
                    {total > 0 && (
                        <div className={styles.jStack}>
                            {JOB_STAGES.map((s) => {
                                const w = ((stageCounts[s.key] || 0) / total) * 100;
                                return w > 0 ? <div key={s.key} style={{ width: `${w}%`, background: s.color }} /> : null;
                            })}
                        </div>
                    )}
                </div>

                <div className={styles.drawerFilterBar}>
                    <span className={styles.drawerFilterLabel}>Show</span>
                    {['all', ...JOB_STAGES.map((s) => s.key)].map((f) => (
                        <button
                            key={f}
                            className={`${styles.jfBtn} ${filter === f ? styles.jfBtnActive : ''}`}
                            onClick={() => { setFilter(f); setExpandedId(null); }}
                        >
                            {f === 'all' ? 'All' : stageByKey(f).short}
                        </button>
                    ))}
                </div>

                <div className={styles.epList}>
                    {loading && <div className={styles.episodeEmpty}>Loading…</div>}
                    {!loading && filtered.length === 0 && (
                        <div className={styles.episodeEmpty}>
                            {episodes.length === 0 ? 'No episodes recorded against this job yet.' : 'No episodes at this stage.'}
                        </div>
                    )}
                    {!loading && filtered.map((ep) => {
                        const s = stageByKey(ep.stage);
                        const isExpanded = expandedId === ep.episode_id;
                        return (
                            <div key={ep.episode_id}>
                                <div className={styles.episodeRow} onClick={() => setExpandedId(isExpanded ? null : ep.episode_id)}>
                                    <span className={styles.epNumber}>#{String(ep.episode_id).padStart(4, '0')}</span>
                                    <div className={styles.epDot} style={{ background: s ? s.color : 'rgba(255,255,255,0.18)' }} />
                                    <div className={styles.epInfo}>
                                        <div className={styles.epInfoTitle}>Episode {ep.episode_id}</div>
                                        <div className={styles.epInfoSub}>{(ep.video_duration_hours * 3600).toFixed(1)}s · {ep.uploaded_at ? ep.uploaded_at.slice(0, 10) : '—'}</div>
                                    </div>
                                    <span className={styles.epBadge} style={{ color: s ? s.color : '#666' }}>{s ? s.label : 'Pending'}</span>
                                </div>
                                {isExpanded && (
                                    <div className={styles.epDetail}>
                                        <div className={styles.epDetailLabel}>Review timeline</div>
                                        {(ep.timeline || []).map((step) => (
                                            <div
                                                key={step.key}
                                                className={`${styles.tlRow} ${step.reached ? '' : styles.tlPending}`}
                                            >
                                                <span
                                                    className={styles.tlDot}
                                                    style={step.reached ? { background: TIMELINE_DOT_COLOR[step.key] } : undefined}
                                                />
                                                <span className={styles.tlLbl}>{step.label}</span>
                                                <span className={styles.tlTime}>
                                                    {step.at ? step.at.slice(0, 10) : (step.reached ? '—' : 'Not yet')}
                                                </span>
                                            </div>
                                        ))}
                                        {((ep.stage === 'rejected' && ep.processing_error) || ep.stage === 'qa_rejected') && (
                                            <>
                                                <div className={styles.epDetailLabel} style={{ marginTop: 10 }}>
                                                    Rejection reason{Array.isArray(ep.processing_error?.details?.failed_checks) && ep.processing_error.details.failed_checks.filter(Boolean).length > 1 ? 's' : ''}
                                                </div>
                                                <div className={styles.epErrList}>
                                                    {getEpisodeRejectionRows(ep, styles)}
                                                    {ep.stage === 'qa_rejected' && <RejectedEpisodePreview episode={ep} episodeId={ep.episode_id} token={getHeaders().Authorization.replace('Bearer ', '')} isAdmin={jobsBaseUrl.includes('/admin/')}/>}
                                                </div>
                                            </>
                                        )}
                                        {ep.stage === 'customer_rejected' && ep.reported_reason && (
                                            <>
                                                <div className={styles.epDetailLabel} style={{ marginTop: 10 }}>Customer feedback</div>
                                                <div className={styles.epErrDesc}>{ep.reported_reason}</div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default JobDetailDrawer;
